/**
 * Enterprise Billing Management Page
 * Epic 86: Enterprise Subscription Management
 *
 * Displays subscription status, seat usage, team management, and billing controls
 * for corporate team managers
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Tile,
  Button,
  Tag,
  ProgressBar,
  Modal,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  Loading,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  NumberInput,
  TextInput,
} from '@carbon/react';
import { Add, TrashCan, ChevronRight } from '@carbon/icons-react';
import './EnterpriseBilling.scss';

interface Subscription {
  id: string;
  plan_name: string;
  status: string;
  billing_interval: string;
  price: number;
  currency: string;
  current_period_end: number;
  seat_count: number;
  cancel_at_period_end: boolean;
  features: string[];
}

interface TeamMember {
  member_id: string;
  email: string;
  role: string;
  status: string;
  invited_at: number;
  joined_at?: number;
}

interface Usage {
  total_ai_tokens_used: number;
  per_member_usage: Array<{ email: string; tokens_used: number }>;
}

interface SubscriptionData {
  subscription: Subscription | null;
  team_members: TeamMember[];
  usage: Usage | null;
  active_members_count: number;
}

// Module-level cache to prevent duplicate fetches across remounts
let dataCache: SubscriptionData | null = null;
let isFetching = false;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute
let mountCounter = 0;

const EnterpriseBillingComponent: React.FC = () => {
  const componentId = React.useRef(++mountCounter);
  const [subscription, setSubscription] = useState<Subscription | null>(dataCache?.subscription || null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(dataCache?.team_members || []);
  const [usage, setUsage] = useState<Usage | null>(dataCache?.usage || null);
  const [activeMembersCount, setActiveMembersCount] = useState(dataCache?.active_members_count || 0);
  const [loading, setLoading] = useState(!dataCache);
  const [processing, setProcessing] = useState(false);

  // Add Seats Modal
  const [addSeatsModalOpen, setAddSeatsModalOpen] = useState(false);
  const [additionalSeats, setAdditionalSeats] = useState(1);

  // Add Members Modal
  const [addMembersModalOpen, setAddMembersModalOpen] = useState(false);
  const [memberEmails, setMemberEmails] = useState('');

  // Remove Member Modal
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [removeImmediate, setRemoveImmediate] = useState(true);

  // Track mount/unmount lifecycle
  React.useEffect(() => {
    console.log(`[EnterpriseBilling #${componentId.current}] MOUNTED`);
    return () => {
      console.log(`[EnterpriseBilling #${componentId.current}] UNMOUNTED`);
    };
  }, []);

  const fetchSubscription = useCallback(async () => {
    console.log(`[EnterpriseBilling #${componentId.current}] fetchSubscription called`);
    const now = Date.now();

    // Use cached data if available and fresh
    if (dataCache && now - lastFetchTime < CACHE_DURATION) {
      console.log('[EnterpriseBilling] Using cached data');
      setSubscription(dataCache.subscription);
      setTeamMembers(dataCache.team_members);
      setUsage(dataCache.usage);
      setActiveMembersCount(dataCache.active_members_count);
      setLoading(false);
      return;
    }

    // If another instance is fetching, wait for it
    if (isFetching) {
      console.log('[EnterpriseBilling] Fetch already in progress, waiting...');
      const startWait = Date.now();
      while (isFetching && Date.now() - startWait < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (dataCache) {
        console.log('[EnterpriseBilling] Using data from concurrent fetch');
        setSubscription(dataCache.subscription);
        setTeamMembers(dataCache.team_members);
        setUsage(dataCache.usage);
        setActiveMembersCount(dataCache.active_members_count);
        setLoading(false);
        return;
      }
    }

    isFetching = true;
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      if (!token) {
        console.error('[EnterpriseBilling] No authentication token found');
        setLoading(false);
        return;
      }

      console.log('[EnterpriseBilling] Fetching from /api/v1/subscriptions/enterprise/current');
      const response = await fetch('/api/v1/subscriptions/enterprise/current', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('[EnterpriseBilling] API response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[EnterpriseBilling] API error:', errorText);
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[EnterpriseBilling] Data received:', data);

      if (data.has_subscription) {
        setSubscription(data.subscription);
        setTeamMembers(data.team_members || []);
        setUsage(data.usage);
        setActiveMembersCount(data.active_members_count || 0);
        dataCache = {
          subscription: data.subscription,
          team_members: data.team_members || [],
          usage: data.usage,
          active_members_count: data.active_members_count || 0,
        };
        lastFetchTime = Date.now();
      } else {
        setSubscription(null);
        setTeamMembers([]);
        setUsage(null);
        setActiveMembersCount(0);
        dataCache = { subscription: null, team_members: [], usage: null, active_members_count: 0 };
        lastFetchTime = Date.now();
      }
    } catch (error) {
      console.error('[EnterpriseBilling] Error fetching subscription:', error);
      setSubscription(null);
      setTeamMembers([]);
      setUsage(null);
      setActiveMembersCount(0);
      dataCache = { subscription: null, team_members: [], usage: null, active_members_count: 0 };
      lastFetchTime = Date.now();
    } finally {
      console.log('[EnterpriseBilling] Setting loading to false');
      setLoading(false);
      isFetching = false;
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleAddSeats = async () => {
    setProcessing(true);
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch('/api/v1/subscriptions/enterprise/add-seats', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ additional_seats: additionalSeats }),
      });

      const data = await response.json();

      if (data.success) {
        setAddSeatsModalOpen(false);
        setAdditionalSeats(1);
        // Clear cache to force refetch
        dataCache = null;
        fetchSubscription();
      } else {
        console.error('Failed to add seats:', data.error);
      }
    } catch (error) {
      console.error('Failed to add seats:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleAddMembers = async () => {
    setProcessing(true);
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      // Parse emails from textarea (comma or newline separated)
      const emails = memberEmails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      const members = emails.map(email => ({ email, role: 'member' }));

      const response = await fetch('/api/v1/subscriptions/enterprise/add-members', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ members }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAddMembersModalOpen(false);
        setMemberEmails('');
        // Clear cache to force refetch
        dataCache = null;
        fetchSubscription();
      } else {
        console.error('Failed to add members:', data.error || data.message);
        alert(data.message || data.error || 'Failed to add members');
      }
    } catch (error) {
      console.error('Failed to add members:', error);
      alert('Failed to add members');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setProcessing(true);
    try {
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      const response = await fetch(`/api/v1/subscriptions/enterprise/remove-member/${memberToRemove.member_id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ immediate: removeImmediate }),
      });

      const data = await response.json();

      if (data.success) {
        setRemoveMemberModalOpen(false);
        setMemberToRemove(null);
        // Clear cache to force refetch
        dataCache = null;
        fetchSubscription();
      }
    } catch (error) {
      console.error('Failed to remove member:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <Loading description="Loading subscription..." withOverlay={false} />;
  }

  if (!subscription) {
    return (
      <div className="enterprise-billing">
        <Tile className="no-subscription-tile">
          <h2 className="cds--type-heading-04">No Active Enterprise Subscription</h2>
          <p>Subscribe to unlock team management, SSO integration, and compliance reporting.</p>
          <Button kind="primary" renderIcon={Add} onClick={() => (window.location.href = '/pricing/enterprise')}>
            Request Demo
          </Button>
        </Tile>
      </div>
    );
  }

  const statusTagType =
    subscription.status === 'active'
      ? 'green'
      : subscription.status === 'trialing'
        ? 'blue'
        : subscription.status === 'past_due'
          ? 'red'
          : 'gray';

  const nextBillingDate = new Date(subscription.current_period_end * 1000).toLocaleDateString();
  const seatUsagePercentage = subscription.seat_count > 0 ? (activeMembersCount / subscription.seat_count) * 100 : 0;
  const seatsRemaining = subscription.seat_count - activeMembersCount;

  // DataTable headers
  const headers = [
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role' },
    { key: 'status', header: 'Status' },
    { key: 'invited_at', header: 'Invited' },
    { key: 'actions', header: 'Actions' },
  ];

  // DataTable rows
  const rows = teamMembers.map(member => ({
    id: member.member_id,
    email: member.email,
    role: member.role,
    status: member.status,
    invited_at: new Date(member.invited_at * 1000).toLocaleDateString(),
    actions: member,
  }));

  return (
    <div className="enterprise-billing">
      <h1 className="cds--type-heading-05">Enterprise Subscription & Billing</h1>

      {/* Subscription Status Card */}
      <Tile className="subscription-status-tile">
        <div className="subscription-header">
          <div>
            <h3 className="cds--type-heading-02">{subscription.plan_name}</h3>
            <Tag type={statusTagType}>{subscription.status.toUpperCase()}</Tag>
          </div>
          <div className="subscription-price">
            ${subscription.price}/{subscription.billing_interval}
          </div>
        </div>

        {/* Seat Usage */}
        <div className="seat-usage">
          <p className="cds--type-body-01">
            Using {activeMembersCount} of {subscription.seat_count} seats
          </p>
          <ProgressBar
            label="Seat Utilization"
            value={seatUsagePercentage}
            max={100}
            helperText={`${seatsRemaining} seats remaining`}
          />
        </div>

        <div className="billing-info">
          <p>Next billing date: {nextBillingDate}</p>
          {subscription.cancel_at_period_end && (
            <p className="cancellation-notice">⚠️ Subscription will cancel on {nextBillingDate}</p>
          )}
        </div>

        <div className="subscription-actions">
          <Button kind="primary" onClick={() => setAddSeatsModalOpen(true)} disabled={processing}>
            Add Seats
          </Button>
          <Button kind="secondary" onClick={() => setAddMembersModalOpen(true)} disabled={processing}>
            Add Members
          </Button>
        </div>
      </Tile>

      {/* Team Members Card */}
      <Tile className="team-members-tile">
        <h3 className="cds--type-heading-02">Team Members</h3>
        <DataTable rows={rows} headers={headers}>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => {
                        if (cell.info.header === 'actions') {
                          const member = cell.value as TeamMember;
                          return (
                            <TableCell key={cell.id}>
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={TrashCan}
                                iconDescription="Remove member"
                                hasIconOnly
                                onClick={() => {
                                  setMemberToRemove(member);
                                  setRemoveMemberModalOpen(true);
                                }}
                                disabled={processing}
                              />
                            </TableCell>
                          );
                        }
                        return <TableCell key={cell.id}>{cell.value}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </Tile>

      {/* Usage Metrics Card */}
      {usage && (
        <Tile className="usage-metrics-tile">
          <h3 className="cds--type-heading-02">Team AI Usage This Month</h3>
          <div className="total-usage">
            <p className="cds--type-body-02">
              Total: {usage.total_ai_tokens_used?.toLocaleString() || 0} AI tokens used
            </p>
          </div>
        </Tile>
      )}

      {/* Features Card */}
      <Tile className="features-tile">
        <h3 className="cds--type-heading-02">Your Plan Includes</h3>
        <ul className="features-list">
          {subscription.features.map((feature, index) => (
            <li key={index}>
              <ChevronRight size={16} /> {feature}
            </li>
          ))}
        </ul>
      </Tile>

      {/* Add Seats Modal */}
      <Modal
        open={addSeatsModalOpen}
        modalHeading="Add Seats to Your Plan"
        primaryButtonText={processing ? 'Adding...' : 'Add Seats'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setAddSeatsModalOpen(false)}
        onRequestSubmit={handleAddSeats}
        primaryButtonDisabled={processing || additionalSeats < 1}
      >
        <p>
          Add more seats to accommodate additional team members. Charges will be prorated for the current billing
          period.
        </p>
        <NumberInput
          id="additional-seats"
          label="Number of seats to add"
          min={1}
          value={additionalSeats}
          onChange={(_e, { value }) => setAdditionalSeats(typeof value === 'number' ? value : parseInt(value, 10) || 1)}
        />
        <p className="cds--type-caption-01" style={{ marginTop: '1rem' }}>
          Current: {subscription.seat_count} seats • New total: {subscription.seat_count + additionalSeats} seats
        </p>
      </Modal>

      {/* Add Members Modal */}
      <Modal
        open={addMembersModalOpen}
        modalHeading="Add Team Members"
        primaryButtonText={processing ? 'Adding...' : 'Send Invites'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setAddMembersModalOpen(false)}
        onRequestSubmit={handleAddMembers}
        primaryButtonDisabled={processing || !memberEmails.trim()}
      >
        <p>Enter email addresses for team members you want to invite. They will receive invitation emails.</p>
        <TextArea
          labelText="Email addresses (comma or line separated)"
          placeholder="user1@company.com, user2@company.com"
          value={memberEmails}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMemberEmails(e.target.value)}
          rows={5}
        />
        <p className="cds--type-caption-01" style={{ marginTop: '1rem' }}>
          Available seats: {seatsRemaining}
        </p>
      </Modal>

      {/* Remove Member Modal */}
      <Modal
        open={removeMemberModalOpen}
        modalHeading="Remove Team Member"
        primaryButtonText={processing ? 'Removing...' : 'Remove Member'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setRemoveMemberModalOpen(false)}
        onRequestSubmit={handleRemoveMember}
        danger
        primaryButtonDisabled={processing}
      >
        {memberToRemove && (
          <>
            <p>Are you sure you want to remove {memberToRemove.email} from your team subscription?</p>
            <RadioButtonGroup
              legendText="When should access be revoked?"
              name="remove-timing"
              valueSelected={removeImmediate ? 'immediate' : 'end_of_period'}
              onChange={value => setRemoveImmediate(value === 'immediate')}
            >
              <RadioButton labelText="Immediately (revoke access now)" value="immediate" id="remove-immediate" />
              <RadioButton labelText="At end of billing period (keep access)" value="end_of_period" id="remove-end" />
            </RadioButtonGroup>
          </>
        )}
      </Modal>
    </div>
  );
};

// Wrap in React.memo to prevent unnecessary remounts
export const EnterpriseBilling = React.memo(EnterpriseBillingComponent);

export default EnterpriseBilling;
